import { z } from "zod";
import { CHANNEL_WIDGET_BATCH_MAX, CHANNEL_WIDGET_SURFACES, channelWidgetAgentChangeSchema, channelWidgetArrangementContentSchema, channelWidgetChangesetResultSchema, channelWidgetSpecSchema, channelWidgetSummarySchema, } from "./channel-widget-artifacts.js";
import { channelWidgetSetApplyInputSchema, channelWidgetSetApplyResultSchema } from "./channel-widget-sets.js";
/*
 * What a running turn asks of a channel's widgets through the tenant API: list them, change them, undo
 * a change, or apply them to other channels. Each request carries the run's lease, and the tenant accepts it only while that attempt
 * holds the run. Who may publish is the tenant's to decide, from the run's actor as they stand when
 * the change is saved (docs/specs/shared-widget-blocks.md, section 4).
 */
const id = z.string().trim().min(1).max(200);
const lease = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
    channelId: id,
};
const toolCallId = z.string().trim().min(1).max(500);
export const workerChannelWidgetRequestSchema = z.discriminatedUnion("operation", [
    z.object({
        ...lease, operation: z.literal("list"),
        /** The widgets whose settings to give in full, beside the list: what a turn reads before it changes one. */
        input: z.object({ specs: z.array(id).max(CHANNEL_WIDGET_BATCH_MAX).optional() }).strict(),
    }).strict(),
    // An arrangement here may leave out its grid and filters: the tenant keeps the published ones (wholeAgentChange).
    z.object({ ...lease, operation: z.literal("change"), toolCallId, input: channelWidgetAgentChangeSchema }).strict(),
    z.object({
        ...lease, operation: z.literal("undo"), toolCallId,
        input: z.object({ changesetId: id, idempotencyKey: z.string().trim().min(8).max(200) }).strict(),
    }).strict(),
    // The request's channel is the pattern: the widgets on its Overview are applied to the channels the input names.
    z.object({ ...lease, operation: z.literal("apply"), toolCallId, input: channelWidgetSetApplyInputSchema }).strict(),
]);
/** An arrangement as a list shows it: the version to arrange against, and where each widget sits now. */
export const channelWidgetArrangementSummarySchema = z.object({
    arrangementId: id,
    surface: z.enum(CHANNEL_WIDGET_SURFACES),
    viewId: id.optional(),
    publishedVersionId: id,
    publishedVersion: z.number().int().positive(),
    content: channelWidgetArrangementContentSchema,
}).strict();
export const workerChannelWidgetResponseSchema = z.discriminatedUnion("operation", [
    z.object({
        operation: z.literal("list"), widgets: z.array(channelWidgetSummarySchema), arrangements: z.array(channelWidgetArrangementSummarySchema),
        /** The published spec of each widget asked for, by widget id; left out when none was asked for. */
        specs: z.record(id, z.object({ versionId: id, spec: channelWidgetSpecSchema }).strict()).optional(),
    }).strict(),
    z.object({ operation: z.literal("change"), result: channelWidgetChangesetResultSchema }).strict(),
    z.object({ operation: z.literal("undo"), result: channelWidgetChangesetResultSchema }).strict(),
    z.object({ operation: z.literal("apply"), result: channelWidgetSetApplyResultSchema }).strict(),
]);
/** Why the tenant did not do what a turn asked of a channel's widgets, by the code its answer carries. */
export const WORKER_CHANNEL_WIDGET_REFUSALS = {
    /** The attempt no longer holds the run. */
    leaseLost: "channel_widgets_lease_lost",
    /** Channel widgets are not on for the organisation. */
    off: "channel_widgets_off",
    channelNotFound: "channel_not_found",
};
